import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Sparkles, Zap, Star, Megaphone, TrendingUp, Eye, Target, Rocket, Award, ArrowRight } from 'lucide-react';
import { logAdEvent, openAdDestination } from '../utils/adTracking';

// 10 Visually Stunning Retro-Style Placeholder Ads
const placeholderAds = [
  {
    id: 1,
    headline: "Advertise Here!",
    subtext: "Get your brand in front of thousands of engaged players",
    gradient: 'from-violet-600 via-purple-600 to-indigo-600',
    iconBg: 'bg-violet-400',
    icon: Sparkles,
    pattern: 'circles'
  },
  {
    id: 2,
    headline: "Your Brand Here",
    subtext: "Premium ad placement available for your business",
    gradient: 'from-amber-500 via-orange-500 to-red-500',
    iconBg: 'bg-amber-300',
    icon: Megaphone,
    pattern: 'stripes'
  },
  {
    id: 3,
    headline: "Boost Your Reach",
    subtext: "Connect with our active gaming community today",
    gradient: 'from-cyan-500 via-blue-600 to-indigo-600',
    iconBg: 'bg-cyan-300',
    icon: Zap,
    pattern: 'dots'
  },
  {
    id: 4,
    headline: "Prime Ad Space",
    subtext: "High-visibility placement for maximum impact",
    gradient: 'from-slate-600 via-slate-700 to-slate-800',
    iconBg: 'bg-slate-400',
    icon: Eye,
    pattern: 'grid'
  },
  {
    id: 5,
    headline: "Stand Out Here",
    subtext: "Showcase your brand to our dedicated audience",
    gradient: 'from-rose-500 via-pink-500 to-fuchsia-500',
    iconBg: 'bg-rose-300',
    icon: Star,
    pattern: 'waves'
  },
  {
    id: 6,
    headline: "Ad Space Available",
    subtext: "Partner with JailBirds and grow your business",
    gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
    iconBg: 'bg-emerald-300',
    icon: Award,
    pattern: 'circles'
  },
  {
    id: 7,
    headline: "Promote Here!",
    subtext: "Reach players who are ready to engage",
    gradient: 'from-orange-500 via-amber-500 to-yellow-500',
    iconBg: 'bg-orange-300',
    icon: Rocket,
    pattern: 'stripes'
  },
  {
    id: 8,
    headline: "Your Ad Goes Here",
    subtext: "Strategic placement for your marketing goals",
    gradient: 'from-blue-600 via-indigo-600 to-violet-600',
    iconBg: 'bg-blue-300',
    icon: Target,
    pattern: 'dots'
  },
  {
    id: 9,
    headline: "Grow With Us",
    subtext: "Join our advertising partners today",
    gradient: 'from-green-500 via-emerald-500 to-teal-500',
    iconBg: 'bg-green-300',
    icon: TrendingUp,
    pattern: 'grid'
  },
  {
    id: 10,
    headline: "Reserve This Spot",
    subtext: "Limited ad inventory - act now!",
    gradient: 'from-fuchsia-500 via-purple-600 to-indigo-600',
    iconBg: 'bg-fuchsia-300',
    icon: Mail,
    pattern: 'waves'
  }
];

const AdSenseAd = ({ 
  adSlot, 
  adFormat = 'auto',
  fullWidthResponsive = true,
  style = {},
  className = '',
  placement = 'unknown'
}) => {
  const [currentAd, setCurrentAd] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleAdClick = (e) => {
    e.preventDefault();
    if (currentAd) {
      logAdEvent({ ad_id: currentAd.id, ad_name: currentAd.headline, ad_type: 'static', placement, kind: 'click' });
    }
    // Open the advertiser post in a NEW TAB so the current page/game stays intact.
    openAdDestination();
  };

  useEffect(() => {
    // Pick a random ad and log the serve; rotate (and re-log) every 25 seconds.
    const serve = () => {
      const idx = Math.floor(Math.random() * placeholderAds.length);
      const ad = placeholderAds[idx];
      setCurrentAd(ad);
      logAdEvent({ ad_id: ad.id, ad_name: ad.headline, ad_type: 'static', placement, kind: 'impression' });
    };
    serve();
    const interval = setInterval(serve, 25000);
    return () => clearInterval(interval);
  }, [placement]);

  useEffect(() => {
    try {
      if (window.adsbygoogle && process.env.NODE_ENV === 'production') {
        window.adsbygoogle.push({});
      }
    } catch (error) {
      console.error('AdSense error:', error);
    }
  }, []);

  if (!currentAd) return null;

  const Icon = currentAd.icon;

  // Pattern backgrounds
  const patterns = {
    circles: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.08) 0%, transparent 40%)',
    stripes: 'repeating-linear-gradient(135deg, transparent, transparent 10px, rgba(255,255,255,0.05) 10px, rgba(255,255,255,0.05) 20px)',
    dots: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
    grid: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
    waves: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'20\' viewBox=\'0 0 100 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M21.184 20c.357-.13.72-.264 1.088-.402l1.768-.661C33.64 15.347 39.647 14 50 14c10.271 0 15.362 1.222 24.629 4.928.955.383 1.869.74 2.75 1.072h6.225c-2.51-.73-5.139-1.691-8.233-2.928C65.888 13.278 60.562 12 50 12c-10.626 0-16.855 1.397-26.66 5.063l-1.767.662c-2.475.923-4.66 1.674-6.724 2.275h6.335zm0-20C13.258 2.892 8.077 4 0 4V2c5.744 0 9.951-.574 14.85-2h6.334zM77.38 0C85.239 2.966 90.502 4 100 4V2c-6.842 0-11.386-.542-16.396-2h-6.225zM0 14c8.44 0 13.718-1.21 22.272-4.402l1.768-.661C33.64 5.347 39.647 4 50 4c10.271 0 15.362 1.222 24.629 4.928C84.112 12.722 89.438 14 100 14v-2c-10.271 0-15.362-1.222-24.629-4.928C65.888 3.278 60.562 2 50 2 39.374 2 33.145 3.397 23.34 7.063l-1.767.662C13.223 10.84 8.163 12 0 12v2z\' fill=\'rgba(255,255,255,0.05)\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")'
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        onClick={handleAdClick}
        data-testid="static-ad"
        key={currentAd.id}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.4 }}
        className={`block relative overflow-hidden rounded-2xl border border-white/10 shadow-xl cursor-pointer ${className}`}
        style={{ ...style }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Gradient Background */}
        <div className={`absolute inset-0 bg-gradient-to-r ${currentAd.gradient}`} />
        
        {/* Pattern Overlay */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: patterns[currentAd.pattern],
            backgroundSize: currentAd.pattern === 'dots' ? '15px 15px' : currentAd.pattern === 'grid' ? '20px 20px' : 'auto'
          }}
        />

        {/* Shine Effect on Hover */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          initial={{ x: '-100%' }}
          animate={{ x: isHovered ? '100%' : '-100%' }}
          transition={{ duration: 0.6 }}
        />

        {/* Content - Mobile Responsive */}
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-3 sm:gap-4 p-4 sm:p-5">
          {/* Icon */}
          <motion.div
            animate={{ scale: isHovered ? 1.1 : 1 }}
            transition={{ duration: 0.2 }}
            className="flex-shrink-0"
          >
            <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl ${currentAd.iconBg} flex items-center justify-center shadow-lg`}>
              <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-gray-800" />
            </div>
          </motion.div>

          {/* Text Content */}
          <div className="flex-1 text-center sm:text-left min-w-0">
            <h4 className="font-heading font-bold text-white text-lg sm:text-xl tracking-tight truncate">
              {currentAd.headline}
            </h4>
            <p className="text-white/80 text-xs sm:text-sm mt-0.5 line-clamp-1">
              {currentAd.subtext}
            </p>
            <p className="text-white/60 text-[10px] sm:text-xs mt-1 flex items-center justify-center sm:justify-start gap-1">
              <Mail className="w-3 h-3" />
              <span className="hidden xs:inline">Learn more — advertise here</span>
              <span className="xs:hidden">Advertise here</span>
            </p>
          </div>

          {/* CTA Button */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex-shrink-0"
          >
            <div className="px-4 py-2 sm:px-5 sm:py-2.5 bg-white rounded-xl shadow-lg flex items-center gap-2 group">
              <span className="text-gray-800 text-sm font-bold whitespace-nowrap">
                Get Info
              </span>
              <ArrowRight className="w-4 h-4 text-gray-600 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </motion.div>
        </div>

        {/* AD Label */}
        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 px-2 py-0.5 bg-black/30 backdrop-blur-sm rounded text-white/70 text-[10px] font-semibold uppercase tracking-wider">
          Ad Space
        </div>

        {/* Bottom info bar */}
        <div className="relative z-10 bg-black/20 backdrop-blur-sm px-4 py-2 border-t border-white/10">
          <p className="text-white/70 text-[11px] sm:text-xs text-center">
            <span className="font-semibold text-white/90">Advertise in JailBirds!</span>
            {' '}Reach our engaged gaming community. Click to learn more.
          </p>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AdSenseAd;
